import { LadderLevel } from "common/NewLook/priceLadder.ts";
import {
  TradeBarOrders,
  TradeBarPoints,
} from "common/NewLook/tradeBarLayout.ts";

export type TradeBarTargetKind = "entry" | "price" | "take_profit" | "stop";

export interface TradeBarTarget {
  kind: TradeBarTargetKind;
  index: number;
}

export type TradeBarOrderRow =
  | { type: "level"; index: number }
  | { type: "group"; from: number; to: number };

export const TRADE_BAR_ROW_LIMIT = 7;

const nearestIndex = (positions: number[], x: number) =>
  positions.reduce(
    (best, position, index) =>
      best < 0 || Math.abs(position - x) < Math.abs(positions[best] - x)
        ? index
        : best,
    -1
  );

const isNear = (x: number, snap: number, position?: number) =>
  position !== undefined && Math.abs(position - x) <= snap;

const isWithin = (positions: number[], x: number, snap: number) =>
  positions.length > 0 &&
  x >= Math.min(...positions) - snap &&
  x <= Math.max(...positions) + snap;

const getProfitBoundary = (points: TradeBarPoints) =>
  points.entries.length
    ? Math.max(...points.entries)
    : points.price ?? points.stop ?? 0;

const getMarkerTarget = (
  points: TradeBarPoints,
  x: number,
  snap: number
): TradeBarTarget | undefined => {
  if (isNear(x, snap, points.price)) return { kind: "price", index: -1 };
  const takeProfit = nearestIndex(points.takeProfits, x);
  if (isNear(x, snap, points.takeProfits[takeProfit]))
    return { kind: "take_profit", index: takeProfit };
  if (isNear(x, snap, points.stop)) return { kind: "stop", index: -1 };
};

export const getTradeBarTarget = (
  points: TradeBarPoints,
  x: number,
  snap: number
): TradeBarTarget => {
  const marker = getMarkerTarget(points, x, snap);
  if (marker) return marker;
  if (isWithin(points.entries, x, snap))
    return { kind: "entry", index: nearestIndex(points.entries, x) };
  const takeProfit = nearestIndex(points.takeProfits, x);
  if (isWithin(points.takeProfits, x, snap) || x > getProfitBoundary(points))
    return { kind: "take_profit", index: takeProfit };
  return { kind: "stop", index: -1 };
};

export const getDefaultTradeBarTarget = (
  orders: TradeBarOrders,
  kind: TradeBarTargetKind
): TradeBarTarget => {
  if (kind === "entry")
    return {
      kind,
      index:
        orders.nextEntryIndex < 0
          ? orders.entries.length - 1
          : orders.nextEntryIndex,
    };
  if (kind === "take_profit")
    return {
      kind,
      index: orders.takeProfits.length ? orders.nextTakeProfitIndex : -1,
    };
  return { kind, index: -1 };
};

export const getTradeBarTargetX = (
  points: TradeBarPoints,
  target: TradeBarTarget
) => {
  if (target.kind === "price") return points.price;
  if (target.kind === "stop") return points.stop;
  const positions =
    target.kind === "entry" ? points.entries : points.takeProfits;
  return positions[target.index];
};

const isFilled = (level: LadderLevel) => level.filled > 0;

const foldRows = (levels: LadderLevel[], kept: Set<number>) => {
  const rows: TradeBarOrderRow[] = [];
  let run: number[] = [];
  const flush = () => {
    if (run.length === 1) rows.push({ type: "level", index: run[0] });
    if (run.length > 1)
      rows.push({ type: "group", from: run[0], to: run[run.length - 1] });
    run = [];
  };
  levels.forEach((level, index) => {
    const breaksRun =
      run.length > 0 && isFilled(levels[run[0]]) !== isFilled(level);
    if (kept.has(index) || breaksRun) flush();
    if (kept.has(index)) rows.push({ type: "level", index });
    else run.push(index);
  });
  flush();
  return rows;
};

export const getTradeBarOrderRows = (
  levels: LadderLevel[],
  focus: number,
  next: number,
  showAll = false
): TradeBarOrderRow[] => {
  if (showAll || levels.length <= TRADE_BAR_ROW_LIMIT)
    return levels.map((_, index) => ({ type: "level", index }));
  return foldRows(levels, new Set([next, focus - 1, focus, focus + 1]));
};
